<template>
  <div class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800 cursor-pointer hover:shadow-lg transition-shadow" @click="$emit('view', post)">
    <div v-if="post.image_url" class="aspect-[4/3] bg-gray-100 dark:bg-gray-900">
      <img :src="post.image_url" class="w-full h-full object-cover">
    </div>
    <div v-else class="aspect-[4/3] bg-gray-100 dark:bg-gray-900 flex items-center justify-center text-gray-400">
      <span v-if="post.status === 'generating'">Generating...</span>
      <span v-else>No image</span>
    </div>

    <div class="p-4">
      <div class="flex justify-between items-start mb-2">
        <div class="flex items-center gap-1.5">
          <span class="text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900 px-2 py-0.5 rounded-full">{{ post.keyword }}</span>
          <span v-if="qaGrade" :class="qaGradeClass" class="text-[10px] font-bold px-1.5 py-0.5 rounded">{{ qaGrade }}</span>
        </div>
        <span :class="statusClass" class="text-xs px-2 py-0.5 rounded font-medium">{{ post.status }}</span>
        <span v-if="post.approval_status" :class="approvalBadgeClass" class="text-xs px-2 py-0.5 rounded font-medium">
          {{ approvalLabel }}
        </span>
      </div>

      <p v-if="post.title" class="font-medium text-sm mb-1 dark:text-white">
        {{ post.title }}
      </p>
      <p class="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
        {{ post.body }}
      </p>

      <!-- Data source indicators -->
      <div v-if="post.status === 'completed' || post.status === 'edited'" class="flex flex-wrap gap-1.5 mt-2">
        <span v-if="post.website_page_id" class="inline-flex items-center gap-1 text-[10px] font-medium text-green-700 bg-green-50 px-1.5 py-0.5 rounded" :title="post.website_page_title">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          Page matched
        </span>
        <span v-else class="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
          No page context
        </span>
        <span v-if="llmLabel" class="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
          {{ llmLabel }}
        </span>
      </div>

      <div class="flex justify-between items-center mt-3 pt-3 border-t dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
        <span>{{ post.scheduled_date }}</span>
        <div class="flex gap-2">
          <button @click.stop="$emit('edit', post)" class="text-primary-500 hover:underline">
            Edit
          </button>
          <button @click.stop="$emit('regenerate', post)" class="text-orange-500 hover:underline">
            Regenerate
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({ post: { type: Object, default: () => null } })
defineEmits(['view', 'edit', 'regenerate'])

const statusClasses = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  generating: 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  edited: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
}
const statusClass = computed(() => statusClasses[props.post.status] || '')

const llmProviders = { anthropic: 'Claude', openai: 'GPT-4o', gemini: 'Gemini' }
const llmLabel = computed(() => {
  const provider = props.post.generation_metadata?.llm_provider
  return provider ? llmProviders[provider] || provider : null
})

const qaGrade = computed(() => props.post.qa_scorecard?.grade)
const qaGradeClasses = {
  A: 'bg-green-100 text-green-800',
  B: 'bg-blue-100 text-blue-800',
  C: 'bg-amber-100 text-amber-800',
  D: 'bg-orange-100 text-orange-800',
  F: 'bg-red-100 text-red-800'
}
const qaGradeClass = computed(() => qaGradeClasses[qaGrade.value] || 'bg-gray-100 text-gray-800')

const approvalLabels = {
  pending: 'Review pending',
  approved: 'Client approved',
  changes_requested: 'Changes requested',
  revised: 'Revised'
}
const approvalLabel = computed(() => approvalLabels[props.post.approval_status] || '')

const approvalBadgeClasses = {
  pending: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  changes_requested: 'bg-amber-100 text-amber-800',
  revised: 'bg-blue-100 text-blue-800'
}
const approvalBadgeClass = computed(() => approvalBadgeClasses[props.post.approval_status] || '')
</script>
